import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ContactModel, Contact, ShareModel, ShareSummary } from "common247";

/**
 * State shape for the contacts slice.
 */
export interface IContactState {
  contacts: ContactModel[];
  allContacts: Contact[];
  sharesByContactId: Record<string, ShareModel[]>;
  loading: boolean;
  loadingShares: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: IContactState = {
  contacts: [],
  allContacts: [],
  sharesByContactId: {},
  loading: false,
  loadingShares: false,
  error: null,
  lastFetched: null,
};

/**
 * Maps a list of shares to a summary list for a contact.
 * @param shares - The list of shares.
 * @returns Array of ShareSummary objects.
 */
function mapSharesToSummaries(shares: ShareModel[]): ShareSummary[] {
  return shares.map((s) => ({
    id: s.targetId,
    displayName: s.targetDisplayName || "Unknown",
    type: s.targetType,
    role: s.role,
    targetUserId: s.targetUserId,
  }));
}

export const contactsSlice = createSlice({
  name: "contacts",
  initialState,
  reducers: {
    /**
     * Sets the loaded contacts and resets loading state.
     */
    setContacts: (
      state,
      action: PayloadAction<{ contacts: ContactModel[]; allContacts: Contact[] }>
    ) => {
      state.contacts = action.payload.contacts;
      state.allContacts = action.payload.allContacts;
      state.loading = false;
      state.error = null;
      state.lastFetched = Date.now();
    },

    /**
     * Upserts a contact in the store.
     */
    updateContact: (state, action: PayloadAction<ContactModel>) => {
      const incoming = action.payload;
      const index = state.contacts.findIndex((c) => c.id === incoming.id);
      if (index !== -1) {
        state.contacts[index] = { ...state.contacts[index], ...incoming };
      } else {
        state.contacts.unshift(incoming);
      }
    },

    /**
     * Removes a contact by ID.
     */
    removeContact: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.contacts = state.contacts.filter((c) => c.id !== id);
      state.allContacts = state.allContacts.filter((c) => c.id !== id);
      delete state.sharesByContactId[id];
    },

    /**
     * Sets cached shares for a specific contact and synchronizes sharedWith summaries.
     */
    setContactShares: (
      state,
      action: PayloadAction<{ contactId: string; shares: ShareModel[] }>
    ) => {
      const { contactId, shares } = action.payload;
      state.sharesByContactId[contactId] = shares;
      state.loadingShares = false;

      const summaries = mapSharesToSummaries(shares);
      const contact = state.contacts.find((c) => c.id === contactId);
      if (contact) {
        contact.sharedWith = summaries;
      }
    },

    /**
     * Updates or adds a single share for a contact.
     */
    updateContactShare: (
      state,
      action: PayloadAction<{ contactId: string; share: ShareModel }>
    ) => {
      const { contactId, share } = action.payload;
      const existing = state.sharesByContactId[contactId] || [];
      const idx = existing.findIndex((s) => s.id === share.id || (s.targetId === share.targetId && s.sourceId === share.sourceId));
      let updatedShares: ShareModel[];
      if (idx !== -1) {
        updatedShares = existing.map((s, i) => (i === idx ? { ...s, ...share } : s));
      } else {
        updatedShares = [...existing, share];
      }
      state.sharesByContactId[contactId] = updatedShares;

      const summaries = mapSharesToSummaries(updatedShares);
      const contact = state.contacts.find((c) => c.id === contactId);
      if (contact) {
        contact.sharedWith = summaries;
      }
    },

    /**
     * Removes a share associated with a contact.
     */
    removeContactShare: (
      state,
      action: PayloadAction<{ contactId?: string; shareId: string; sourceId?: string }>
    ) => {
      const { shareId, contactId, sourceId } = action.payload;
      const targetContactId = contactId || sourceId;

      if (targetContactId && state.sharesByContactId[targetContactId]) {
        const remaining = state.sharesByContactId[targetContactId].filter((s) => s.id !== shareId);
        state.sharesByContactId[targetContactId] = remaining;

        const summaries = mapSharesToSummaries(remaining);
        const contact = state.contacts.find((c) => c.id === targetContactId);
        if (contact) {
          contact.sharedWith = summaries;
        }
      } else {
        // Search across all cached contact shares if specific contactId was not provided
        Object.keys(state.sharesByContactId).forEach((cId) => {
          const shares = state.sharesByContactId[cId];
          if (shares && shares.some((s) => s.id === shareId)) {
            const remaining = shares.filter((s) => s.id !== shareId);
            state.sharesByContactId[cId] = remaining;
            const contact = state.contacts.find((c) => c.id === cId);
            if (contact) {
              contact.sharedWith = mapSharesToSummaries(remaining);
            }
          }
        });
      }
    },

    /**
     * Sets contacts loading indicator.
     */
    setContactsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    /**
     * Sets sharing data loading indicator.
     */
    setLoadingShares: (state, action: PayloadAction<boolean>) => {
      state.loadingShares = action.payload;
    },

    /**
     * Sets contacts error state.
     */
    setContactsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    /**
     * Invalidates contacts cache timestamp.
     */
    invalidateContacts: (state) => {
      state.lastFetched = null;
    },
  },
});

export const {
  setContacts,
  updateContact,
  removeContact,
  setContactShares,
  updateContactShare,
  removeContactShare,
  setContactsLoading,
  setLoadingShares,
  setContactsError,
  invalidateContacts,
} = contactsSlice.actions;

export default contactsSlice.reducer;
